import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import useMaterialsMasterDataInit from "./useMaterialsMasterDataInit";
import { useMaterialUpdate } from "./useMaterialUpdate";
import { useDatabaseSync } from "./useDatabaseSync";
import { useFormulas, useInspectionAttributes, useProductDependencies } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { toLegacyDependencyRules } from "../../../../types/productDependencies";
import { calculateQuantity, recalculateDependentQuantities } from "./utils/productHelpers";
import {
    buildLinesFromFurnaceInspection,
    isMultiLineCategory,
    isMultiLineSelection,
    materialSelectionsSignature,
} from "./utils/materialLineHelpers";
import { useMaterialLineUpdate } from "./useMaterialLineUpdate";

export function useMaterials({ measurements }: { measurements: any }) {
    const { project } = useProject();
    const { useList } = useFormulas();
    const { data: formulas } = useList();
    const { useList: useListDependencies } = useProductDependencies();
    const { data: dependenciesConfig, isLoading: isDependenciesLoading } = useListDependencies();
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const listAttributesSvc = useListAttributes();
    const [attributes, setAttributes] = useState<any[]>([]);
    const [attributesReady, setAttributesReady] = useState(false);

    const legacyDependencies = useMemo(
        () => (dependenciesConfig ? toLegacyDependencyRules(dependenciesConfig) : []),
        [dependenciesConfig],
    );

    useEffect(() => {
        if (!project?.id) return;
        setAttributesReady(false);
        listAttributesSvc
            .mutateAsync({ ProjectId: project.id })
            .then((data) => {
                setAttributes(Array.isArray(data) ? data : []);
                setAttributesReady(true);
            })
            .catch((error) => {
                console.error(error);
                setAttributesReady(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    const [materialSchema, setMaterialSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [identifiedProducts, setIdentifiedProducts] = useState<any>(null);
    const [missingProducts, setMissingProducts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedValues, setSelectedValues] = useState<Record<string, any>>({});
    const [openAdditionalProductDialog, setOpenAdditionalProductDialog] = useState(false);

    const { initializeDbState, syncToDatabase } = useDatabaseSync();
    const syncReadyRef = useRef(false);
    const skipDebouncedSyncRef = useRef(false);
    const lastHydratedSignatureRef = useRef("");
    const syncToDatabaseRef = useRef(syncToDatabase);
    syncToDatabaseRef.current = syncToDatabase;

    const debouncedSyncToDatabase = useRef(
        debounce((values: Record<string, any>) => {
            void syncToDatabaseRef.current(values);
        }, 400),
    ).current;

    useEffect(() => () => debouncedSyncToDatabase.cancel(), [debouncedSyncToDatabase]);

    const applyHydratedSelections = useCallback(
        (values: Record<string, any>) => {
            const signature = materialSelectionsSignature(values);
            if (signature === lastHydratedSignatureRef.current) return;
            lastHydratedSignatureRef.current = signature;

            skipDebouncedSyncRef.current = true;
            setSelectedValues(values);
            initializeDbState(values);
            syncReadyRef.current = true;
        },
        [initializeDbState],
    );

    useEffect(() => {
        if (!syncReadyRef.current) return;

        if (skipDebouncedSyncRef.current) {
            skipDebouncedSyncRef.current = false;
            void syncToDatabaseRef.current(selectedValues);
            return;
        }

        debouncedSyncToDatabase(selectedValues);
    }, [selectedValues, debouncedSyncToDatabase]);

    const { handleQuantityUpdate, handleClearProduct, handleProductChange, handleColorChange } = useMaterialUpdate({
        materialSchema,
        measurements,
        formulas,
        attributes,
        dependencies: legacyDependencies,
        setSelectedValues,
    });

    const { handleLineProductChange, handleLineQuantityUpdate, handleAddLine, handleRemoveLine, handleClearMultiLine } =
        useMaterialLineUpdate({ materialSchema, setSelectedValues });

    const handleReevaluateFormula = useCallback(
        (category: string) => {
            if (isMultiLineCategory(category)) {
                setSelectedValues((prev) => {
                    const existingLines = isMultiLineSelection(prev[category]) ? prev[category].lines : [];
                    const lines = buildLinesFromFurnaceInspection(materialSchema, category, attributes, existingLines);
                    if (lines.length === 0) return prev;
                    return { ...prev, [category]: { multiLine: true, lines } };
                });
                return;
            }

            if (!formulas) return;

            setSelectedValues((prev) => {
                const sel = prev[category];
                if (!sel?.value) return prev;

                const next = { ...prev };
                const calculatedQty = calculateQuantity(
                    materialSchema,
                    measurements,
                    category,
                    sel.value,
                    formulas,
                    attributes,
                    next,
                );
                next[category] = { ...sel, quantity: calculatedQty };

                recalculateDependentQuantities(
                    next,
                    category,
                    legacyDependencies,
                    materialSchema,
                    measurements,
                    formulas,
                    attributes,
                );

                return next;
            });
        },
        [materialSchema, measurements, formulas, attributes, legacyDependencies],
    );

    const { loadMasterData, fetchCartItems, calculatePriceRanges } = useMaterialsMasterDataInit({
        measurements,
        formulas,
        attributes,
        attributesReady,
        dependencies: legacyDependencies,
        materialSchema,
        setMaterialSchema,
        setCart,
        setPriceRanges,
        setIdentifiedProducts,
        setMissingProducts,
        setIsLoading,
        applyHydratedSelections,
    });

    return {
        materialSchema,
        cart,
        priceRanges,
        identifiedProducts,
        missingProducts,
        isLoading: isLoading || isDependenciesLoading,
        selectedValues,
        openAdditionalProductDialog,
        setOpenAdditionalProductDialog,
        loadMasterData,
        fetchCartItems,
        calculatePriceRanges,
        handleQuantityUpdate,
        handleClearProduct,
        handleProductChange,
        handleColorChange,
        handleReevaluateFormula,
        handleLineProductChange,
        handleLineQuantityUpdate,
        handleAddLine,
        handleRemoveLine,
        handleClearMultiLine,
    };
}
